import { useState } from "react";
import { Link } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Legend
} from "recharts";
import {
  ArrowLeft, Package, TrendingUp, Building2, Activity,
  ShieldCheck, Clock, Award, Truck, FileText, Box, MapPin, Star, Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import NeuroSpongePDF from "@/components/NeuroSpongePDF";

// ========================================
// DATA — Sterile Mate Neuro Sponge Sales
// 200 units per case
// ========================================

const UNITS_PER_CASE = 200;

// Cases sold by SKU (2025)
const SALES_BY_SKU = [
  { sku: '½" × ½" w/ Locator String', cases: 82, units: 82 * UNITS_PER_CASE, color: "#0e7490" },
  { sku: '½" × 3" w/ Locator String', cases: 91, units: 91 * UNITS_PER_CASE, color: "#0284c7" },
  { sku: '¼" × 6" No String', cases: 30, units: 30 * UNITS_PER_CASE, color: "#d97706" },
  { sku: '½" × 6" No String', cases: 34, units: 34 * UNITS_PER_CASE, color: "#ea580c" },
  { sku: '¾" × 6" No String', cases: 38, units: 38 * UNITS_PER_CASE, color: "#16a34a" },
  { sku: '1" × 6" No String', cases: 30, units: 30 * UNITS_PER_CASE, color: "#059669" },
];
const TOTAL_CASES_SOLD = 305;
const TOTAL_UNITS = TOTAL_CASES_SOLD * UNITS_PER_CASE;

// Sales by region
const SALES_BY_REGION = [
  { name: "Texas Medical Center (7 Systems)", cases: 265, pct: 87, color: "#0e7490" },
  { name: "Louisiana", cases: 40, pct: 13, color: "#d97706" },
];

// Sales by product type
const SALES_BY_TYPE = [
  { name: "With Locator String", cases: 173, pct: 57, color: "#0e7490" },
  { name: "Without String (Specialty)", cases: 132, pct: 43, color: "#d97706" },
];

// Year-over-year sales growth since 2020
const YOY_SALES = [
  { year: "2020", cases: 45 },
  { year: "2021", cases: 95 },
  { year: "2022", cases: 145 },
  { year: "2023", cases: 195 },
  { year: "2024", cases: 250 },
  { year: "2025", cases: 305 },
];

// Product catalog
const PRODUCT_CATALOG = [
  {
    category: "With Locator String",
    description: "Standard neurosurgical sponge patties with radiopaque locator string for intraoperative tracking and X-ray detection.",
    products: [
      { size: '½" × ½"', stringType: "Locator String", cases: 82, units: 82 * UNITS_PER_CASE },
      { size: '½" × 3"', stringType: "Locator String", cases: 91, units: 91 * UNITS_PER_CASE },
    ],
  },
  {
    category: "Without String (Specialty Sizes)",
    description: "Specialty neuro sponge patties without locator string for cranial and complex neurosurgical procedures.",
    products: [
      { size: '¼" × 6"', stringType: "No String", cases: 30, units: 30 * UNITS_PER_CASE },
      { size: '½" × 6"', stringType: "No String", cases: 34, units: 34 * UNITS_PER_CASE },
      { size: '¾" × 6"', stringType: "No String", cases: 38, units: 38 * UNITS_PER_CASE },
      { size: '1" × 6"', stringType: "No String", cases: 30, units: 30 * UNITS_PER_CASE },
    ],
  },
];

// Custom pie label
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, pct }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-sm font-bold">
      {pct}%
    </text>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-white text-sm shadow-xl">
        <p className="font-bold">{d.name || d.sku || d.year}</p>
        {d.cases !== undefined && <p className="text-cyan-300">{d.cases.toLocaleString()} cases ({(d.cases * UNITS_PER_CASE).toLocaleString()} units)</p>}
      </div>
    );
  }
  return null;
};

const BarTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-white text-sm shadow-xl">
        <p className="font-bold">{d.sku}</p>
        <p className="text-cyan-300">{d.cases} cases sold</p>
        <p className="text-amber-300">{d.units.toLocaleString()} units (200/case)</p>
      </div>
    );
  }
  return null;
};

export default function NeuroSpongeDistribution() {
  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="neuro-sponge-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] via-[#0056A8] to-[#003366] border-b border-blue-500/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/admin" className="inline-flex items-center gap-2 text-blue-200 hover:text-white text-sm mb-4 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em] mb-2">Sterile Mate LLC — Third-Party Distribution</p>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Neurosurgical Sponge Patties — Sales & Distribution
              </h1>
              <p className="text-blue-200 mt-2 max-w-2xl">
                Major secondary supplier in the Texas Medical Center since 2020. Serving 7 healthcare systems in Texas and facilities in Louisiana. Largest client: U.S. Government.
              </p>
            </div>
            <div className="hidden md:flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full">
                <Award className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-white">TMC Distributor Since 2020</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
                <Star className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-amber-300">Largest Client: U.S. Government</span>
              </div>
              <PDFDownloadLink document={<NeuroSpongePDF />} fileName="Sterile_Mate_Neuro_Sponge_Distribution_Report.pdf">
                {({ loading }) => (
                  <Button className="bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-full px-4 py-2 h-auto" data-testid="download-pdf-btn">
                    <Download className="w-4 h-4 mr-2" />
                    <span className="text-sm font-semibold">{loading ? "Preparing PDF..." : "Download Report PDF"}</span>
                  </Button>
                )}
              </PDFDownloadLink>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-10">

        {/* ========== STAT CARDS ========== */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stat-cards">
          {[
            { label: "Cases Sold (2025)", value: TOTAL_CASES_SOLD.toLocaleString(), icon: Package, color: "text-amber-400", border: "border-amber-500/30" },
            { label: "Total Units Sold", value: TOTAL_UNITS.toLocaleString(), sub: "200 units/case", icon: Box, color: "text-cyan-400", border: "border-cyan-500/30" },
            { label: "TX Health Systems", value: "7", icon: Building2, color: "text-emerald-400", border: "border-emerald-500/30" },
            { label: "States Served", value: "TX + LA", icon: MapPin, color: "text-violet-400", border: "border-violet-500/30" },
            { label: "Growth Since 2020", value: "578%", icon: TrendingUp, color: "text-rose-400", border: "border-rose-500/30" },
          ].map((stat) => (
            <div key={stat.label} className={`bg-slate-800/50 border ${stat.border} rounded-xl p-5`}>
              <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
              <p className="text-2xl font-black text-white">{stat.value}</p>
              <p className="text-xs text-slate-400 mt-1">{stat.label}</p>
              {stat.sub && <p className="text-[10px] text-slate-500 mt-0.5">{stat.sub}</p>}
            </div>
          ))}
        </div>

        {/* ========== ROW 1: Cases Sold by SKU + Region Pie ========== */}
        <div className="grid lg:grid-cols-3 gap-6">

          {/* Bar Chart — Cases by SKU */}
          <Card className="bg-slate-800/50 border-slate-700/50 lg:col-span-2" data-testid="sales-bar-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-amber-400" />
                2025 Cases Sold by Product Size
              </CardTitle>
              <p className="text-sm text-slate-400">{TOTAL_CASES_SOLD} cases ({TOTAL_UNITS.toLocaleString()} units) across all sizes | 200 units per case</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={SALES_BY_SKU} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="sku" stroke="#94a3b8" fontSize={11} angle={-12} textAnchor="end" height={55} />
                  <YAxis stroke="#94a3b8" fontSize={12} label={{ value: "Cases", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="cases" radius={[6, 6, 0, 0]}>
                    {SALES_BY_SKU.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Pie — Sales by Region */}
          <Card className="bg-slate-800/50 border-slate-700/50" data-testid="region-pie-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-emerald-400" />
                Sales by Region
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={SALES_BY_REGION}
                    cx="50%" cy="50%"
                    outerRadius={85}
                    innerRadius={40}
                    dataKey="cases"
                    label={renderPieLabel}
                    labelLine={false}
                  >
                    {SALES_BY_REGION.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} stroke="#0a0f1a" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 mt-3 w-full">
                {SALES_BY_REGION.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-slate-300">{item.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-white">{item.cases} cases</span>
                  </div>
                ))}
                <div className="border-t border-slate-700 pt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-300">Total</span>
                  <span className="text-sm font-bold text-cyan-400">{TOTAL_CASES_SOLD} cases</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ========== ROW 2: YOY Growth ========== */}
        <Card className="bg-slate-800/50 border-slate-700/50" data-testid="yoy-growth-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Annual Sales Growth (2020–2025)
            </CardTitle>
            <p className="text-sm text-slate-400">Cases sold per year since entering the Texas Medical Center as a third-party distributor</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={YOY_SALES} margin={{ top: 10, right: 30, left: 20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCases" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0e7490" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#0e7490" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} label={{ value: "Cases Sold", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cases" name="Cases Sold" stroke="#0e7490" fill="url(#colorCases)" strokeWidth={2.5} dot={{ fill: "#0e7490", r: 4 }} activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* ========== ROW 3: Product Catalog ========== */}
        <div className="space-y-6" data-testid="product-catalog">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            Product Catalog & 2025 Sales
          </h2>

          {PRODUCT_CATALOG.map((section) => (
            <Card key={section.category} className="bg-slate-800/50 border-slate-700/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">{section.category}</CardTitle>
                <p className="text-sm text-slate-400">{section.description}</p>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-t border-slate-700/50">
                      <th className="text-left text-xs font-semibold text-slate-400 px-5 py-3">Product Size</th>
                      <th className="text-left text-xs font-semibold text-slate-400 px-5 py-3">String Type</th>
                      <th className="text-right text-xs font-semibold text-slate-400 px-5 py-3">Cases Sold</th>
                      <th className="text-right text-xs font-semibold text-slate-400 px-5 py-3">Total Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.products.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-700/30 hover:bg-slate-700/20">
                        <td className="text-sm font-medium text-white px-5 py-3">{item.size}</td>
                        <td className="text-sm text-slate-300 px-5 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.stringType === "Locator String" ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" : "bg-amber-500/20 text-amber-300 border border-amber-500/30"}`}>
                            {item.stringType}
                          </span>
                        </td>
                        <td className="text-sm font-bold text-white text-right px-5 py-3">{item.cases}</td>
                        <td className="text-sm text-slate-400 text-right px-5 py-3">{item.units.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-600">
                      <td colSpan={2} className="text-sm font-semibold text-slate-300 px-5 py-3">Subtotal</td>
                      <td className="text-sm font-bold text-cyan-400 text-right px-5 py-3">
                        {section.products.reduce((sum, p) => sum + p.cases, 0)}
                      </td>
                      <td className="text-sm font-bold text-cyan-400 text-right px-5 py-3">
                        {section.products.reduce((sum, p) => sum + p.units, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          ))}

          {/* Grand total row */}
          <div className="flex items-center justify-between bg-slate-800/80 border border-cyan-500/30 rounded-xl px-6 py-4">
            <span className="text-lg font-bold text-white">2025 Grand Total</span>
            <div className="flex items-center gap-8">
              <div className="text-right">
                <p className="text-2xl font-black text-cyan-400">{TOTAL_CASES_SOLD}</p>
                <p className="text-xs text-slate-400">Cases Sold</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-amber-400">{TOTAL_UNITS.toLocaleString()}</p>
                <p className="text-xs text-slate-400">Total Units</p>
              </div>
            </div>
          </div>
        </div>

        {/* ========== CAPABILITIES STATEMENT ========== */}
        <div className="bg-gradient-to-r from-[#003366] to-[#0056A8] rounded-xl p-8 border border-blue-500/20" data-testid="capabilities-statement">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold text-white mb-4">Why Health Systems Trust Sterile Mate</h3>
              <p className="text-blue-200 mb-4">
                Since 2020, Sterile Mate has served as a dependable secondary source for neurosurgical sponge patties when primary distributors face backorders, allocation limits, or lead time delays. We maintain inventory across all 6 standard and specialty sizes, serving 7 healthcare systems in the Texas Medical Center and facilities in Louisiana. Our largest client is the U.S. Government.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Same-day and emergency fulfillment",
                  "All 6 SKU sizes in continuous inventory",
                  "Competitive third-party pricing",
                  "7 TMC health systems + Louisiana",
                  "U.S. Government contract supplier",
                  "Direct delivery to surgical supply rooms",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                    <span className="text-sm text-blue-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-5xl font-black text-white">{TOTAL_CASES_SOLD}</p>
              <p className="text-sm text-blue-200 mt-1">Cases Sold in 2025</p>
              <p className="text-xs text-blue-300 mt-1">{TOTAL_UNITS.toLocaleString()} individual units</p>
              <div className="mt-4 space-y-2">
                <div className="px-4 py-1.5 bg-white/10 rounded-full">
                  <p className="text-xs text-cyan-300 font-semibold">Texas Medical Center</p>
                </div>
                <div className="px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full">
                  <p className="text-xs text-amber-300 font-semibold">Largest Client: U.S. Government</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center py-6 border-t border-slate-800">
          <p className="text-slate-500 text-sm">
            Sterile Mate LLC — Serving Houston, Texas and Surrounding Communities
          </p>
          <p className="text-slate-500 text-sm mt-1">
            Office: 800.988.3892 | Direct: 281.456.3458 | Mobile: 323.243.9139 | george.broughton@sterilemate.com
          </p>
          <p className="text-slate-600 text-xs mt-2">
            <a href="https://www.sterilemate.com" className="hover:text-white transition-colors">www.sterilemate.com</a>
            {" | "}
            <a href="https://sterilemate.com" className="hover:text-white transition-colors">AskIsaac.io</a>
          </p>
        </div>
      </div>
    </div>
  );
}
